// Faktury section — one card per priced side (load / unload / transport).
//
// Each card: invoice number as the title (link), side/direction/status chips in
// the subtitle, gross right-aligned. When a priced side has no invoice yet, a
// placeholder card offers "Wystaw fakturę" (issue) — wired to the workflow's
// single-side generation.
//
// The invoice links are fetched by the framework: the config declares this as a
// `kind:"data"` section whose query derives the SELECT from the
// invoice_resolved_links presentation (the VIEW — transport-leg links carry no
// transaction_id on the base table). Rows arrive here as `links`.

import React from "react";
import { useTranslation } from "react-i18next";
import type { Transaction, Transport } from "@shared/types/transaction";
import type { ResolvedLinkWithInvoice } from "@shared/types/invoice";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { Eyebrow } from "@ds/display/Eyebrow";
import { TextValue } from "@ds/display/TextValue";
import { AmountDisplay } from "@ds/display/AmountDisplay";
import { Button } from "@ds/primitives/Button";
import { EntityLink } from "@ds/connected/EntityLink";
import { InvoiceDirectionTag, InvoiceReviewStatusTag } from "@ds/domain/EnumTag";
import { useSubQuery } from "@ds/data/subQueriesContext";
import { useGate } from "../../../hooks/useRoleGuard";
import { useTransactionWorkflow } from "../hooks/transactionWorkflowContext";

export interface TransactionFakturyProps {
  transaction: Transaction;
  /** Invoice links fetched by the framework (kind:"data" custom layout). */
  links: ResolvedLinkWithInvoice[];
}

type Slot = {
  key: string;
  side: "load" | "unload";
  label: string;
  links: ResolvedLinkWithInvoice[];
  /** > 0 → "Wystaw fakturę" button; < 0 → "Oczekuje na fakturę" placeholder. */
  priceSign: "positive" | "negative" | "zero";
  price: number | null;
  currency: string | null;
};

// ─── One side/leg card ──────────────────────────────────────────────────────

function FakturaCard({
  label, side, links, priceSign, price, currency, onIssue, issuing,
}: {
  label: string;
  side: "load" | "unload" | "transport";
  links: ResolvedLinkWithInvoice[];
  priceSign: "positive" | "negative" | "zero";
  price?: number | null;
  currency?: string | null;
  onIssue?: () => void;
  issuing?: boolean;
}) {
  const { t } = useTranslation();
  // price === 0 (or null) → no card at all.
  if (priceSign === "zero" && links.length === 0) return null;
  const unique = links.filter(
    (l, i, arr): l is ResolvedLinkWithInvoice & { invoice: NonNullable<ResolvedLinkWithInvoice["invoice"]> } =>
      !!l.invoice && arr.findIndex((x) => x.invoice_id === l.invoice_id) === i,
  );

  const tone = side === "load" ? "green" : side === "unload" ? "blue" : "grey";

  return (
    <Box size="md" border shape="rounded-lg" tone={tone}>
      <Stack gap="sm">
        <Eyebrow>{label}</Eyebrow>

        {unique.length > 0 ? (
          unique.map((l) => {
            const inv = l.invoice;
            return (
              <Inline key={l.id} justify="between" align="center" gap="md" wrap>
                <Inline gap="xs" align="center" wrap>
                  <EntityLink type="invoice" id={l.invoice_id} label={inv.invoice_number ?? `#${l.invoice_id}`} variant="inline" />
                  {inv.direction && <InvoiceDirectionTag value={inv.direction} />}
                  <InvoiceReviewStatusTag value={inv.review_status} />
                </Inline>
                <AmountDisplay value={inv.gross_amount ?? null} currency={inv.currency ?? "PLN"} strong signedNeutral />
              </Inline>
            );
          })
        ) : (
          <Inline justify="between" align="center" gap="md" wrap>
            {priceSign === "positive" && onIssue ? (
              <Button
                label={t("transactions.faktury.issue", "Wystaw fakturę")}
                size="sm"
                onClick={onIssue}
                loading={issuing}
                testId={`issue-invoice-${side}`}
              />
            ) : (
              <TextValue tone="faint" italic>{t("transactions.faktury.waiting", "Oczekuje na fakturę")}</TextValue>
            )}
            {priceSign !== "zero" && <AmountDisplay value={price ?? null} currency={currency ?? "PLN"} strong signedNeutral />}
          </Inline>
        )}
      </Stack>
    </Box>
  );
}

// ─── Section ────────────────────────────────────────────────────────────────

export const TransactionFaktury: React.FC<TransactionFakturyProps> = ({ transaction: tx, links }) => {
  const { t } = useTranslation();
  const { generateInvoiceForTransactions, preparingInvoiceGroupKey } = useTransactionWorkflow();
  const { rows: transports } = useSubQuery<Transport>("transports", tx.id);
  // `fakturownia-proxy` rejects anything outside `invoice_edit` with a 403, so
  // for sales this button was already dead. It also actively misleads now that
  // invoice reads are scoped: a side whose invoice exists but belongs to another
  // rep's client renders as an empty slot, and "Wystaw fakturę" would invite a
  // duplicate. Without `onIssue` the card falls back to "Oczekuje na fakturę".
  const canIssue = useGate("invoice_edit");

  const signOf = (n: number | null | undefined): "positive" | "negative" | "zero" => {
    const v = n ?? 0;
    if (v > 0) return "positive";
    if (v < 0) return "negative";
    return "zero";
  };

  const sideSlots: Slot[] = [
    { key: "load",   side: "load",   label: t("transactions.parties.load_title", "Dostawca · Załadunek"),   links: links.filter((l) => l.side === "load"),   priceSign: signOf(tx.load_price),   price: tx.load_price ?? null,   currency: tx.load_price_currency },
    { key: "unload", side: "unload", label: t("transactions.parties.unload_title", "Odbiorca · Rozładunek"), links: links.filter((l) => l.side === "unload"), priceSign: signOf(tx.unload_price), price: tx.unload_price ?? null, currency: tx.unload_price_currency },
  ];

  return (
    <Stack gap="md">
      {sideSlots.map((s) => {
        const groupKey = `tx-${tx.id}-${s.side}`;
        return (
          <FakturaCard
            key={s.key}
            label={s.label}
            side={s.side}
            links={s.links}
            priceSign={s.priceSign}
            price={s.price}
            currency={s.currency}
            issuing={preparingInvoiceGroupKey === groupKey}
            onIssue={canIssue
              ? () => generateInvoiceForTransactions([tx.id], s.side, groupKey)
              : undefined}
          />
        );
      })}
      {transports.map((tp) => (
        <FakturaCard
          key={`transport-${tp.id}`}
          label={t("transactions.faktury.transport", "Transport")}
          side="transport"
          links={links.filter((l) => l.entity_type === "transport" && l.entity_id === tp.id)}
          priceSign={signOf(tp.price)}
          price={tp.price ?? null}
          currency={tp.price_currency ?? null}
        />
      ))}
    </Stack>
  );
};
